/**
 * Pure allocation math for the Delivery modal's schedule-allocation table.
 *
 * Splits a schedule into three non-negative buckets:
 *   - prior:   sum of OTHER GRN allocations already parked on this schedule
 *   - thisGrn: this allocation's allocated_qty
 *   - balance: remaining unfulfilled qty (schedule_qty - prior - thisGrn)
 *   - overDelivery: positive when prior + thisGrn exceeds schedule_qty
 *
 * Does NOT trust `schedule.shipped_qty` / `schedule.balance_qty` on the
 * allocation row. Those are derived-fields on POLineSchedule and can lag
 * behind the allocation ledger (see the DE schedule-line import's prior
 * shipped_qty clobber bug). The allocation ledger itself
 * (`allocated_qty` + `sibling_allocations`) is the authoritative source
 * of truth: we reconstruct shipped_qty from the ledger rather than
 * reading it.
 *
 * All outputs are clamped at zero — negative prior / balance would be
 * both semantically impossible and a UI regression.
 */
export interface AllocationRowInput {
  schedule_qty: string | number | null | undefined;
  allocated_qty: string | number | null | undefined;
  sibling_allocations?: Array<{ allocated_qty: string | number }> | null;
}

export interface AllocationSplit {
  sched: number;
  prior: number;
  thisGrn: number;
  balance: number;
  overDelivery: number;
}

export function computeAllocationSplit(a: AllocationRowInput): AllocationSplit {
  const sched = a.schedule_qty != null ? Number(a.schedule_qty) : 0;
  const thisGrn = Number(a.allocated_qty ?? 0);
  const siblings = a.sibling_allocations ?? [];
  const prior = siblings.reduce(
    (acc, s) => acc + Number(s.allocated_qty ?? 0),
    0,
  );
  const totalAllocated = prior + thisGrn;
  return {
    sched,
    prior: Math.max(0, prior),
    thisGrn: Math.max(0, thisGrn),
    balance: Math.max(0, sched - totalAllocated),
    overDelivery: Math.max(0, totalAllocated - sched),
  };
}
